import candsLogo from "/img/integration/cands.png"
import { ConnectLogo } from "../connect-logo"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input, PasswordInput } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { use_connect_redirect_to } from "../use_connect_redirect_to"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { LoadingSpinner } from "@/app/loading"
import { useAsyncEffect } from "@/utils"
import { upsertIntegration, getIntegrations, findIntegrationByName } from "@/api/integration"

const CANDS_DISPUTE_PORTAL_URL = "https://vcm.cswg.com/"

/** Prefer an existing dispute-portal username; otherwise suggest the vendor portal username. */
export function disputePortalUsernamePrefill(
  disputeUsername: string | null | undefined,
  vendorUsername: string | null | undefined
): string {
  const dispute = disputeUsername?.trim()
  if (dispute) return dispute
  return vendorUsername?.trim() || ""
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function ConnectCandsDisputePortalPage(props: any = {}) {
  const redirectTo = use_connect_redirect_to(props.redirectTo)
  const candsConnectPath: string = props.candsConnectPath ?? "/setup/connect/cands"
  const [, setLocation] = useLocation()
  const [loading, setLoading] = useState(false)
  const [hasCandsIntegration, setHasCandsIntegration] = useState(false)
  const [loadingIntegration, setLoadingIntegration] = useState(true)

  const form = useFormState({
    username: "",
    password: "",
  })

  useAsyncEffect(async () => {
    const res = await getIntegrations()
    if (res.ok) {
      const candsIntegration = findIntegrationByName(res.value.data, "cands")
      const disputeIntegration = findIntegrationByName(res.value.data, "cands-dispute-portal")
      if (candsIntegration) {
        setHasCandsIntegration(true)
        form.set_form_data({
          username: disputePortalUsernamePrefill(
            disputeIntegration?.username,
            candsIntegration.username
          ),
        })
      }
    }
    setLoadingIntegration(false)
  }, [])

  async function onSubmit() {
    const username = form.username.value.trim()
    if (!username) {
      form.set_form_errors(["Username is required"])
      return
    }
    setLoading(true)
    form.set_form_errors([])
    const res = await upsertIntegration("cands-dispute-portal", username, {
      password: form.password.value,
    })
    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to save credentials. Please try again."])
      return
    }
    setLocation(redirectTo)
  }

  if (loadingIntegration) {
    return (
      <CenterColumnContainer>
        <div class="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      </CenterColumnContainer>
    )
  }

  if (!hasCandsIntegration) {
    return (
      <CenterColumnContainer>
        <div class="text-center py-8">
          <p class="text-gray-500 mb-4">No C&S integration found. Please connect C&S first.</p>
          <Button
            class="bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            onClick={() => setLocation(candsConnectPath)}>
            Connect C&S
          </Button>
        </div>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer>
      <Form class="space-y-6" onSubmit={onSubmit} alerts={form.form_alerts}>
        <ConnectLogo src={candsLogo} alt="C&S" />
        <p class="text-gray-500 text-sm">
          Enter your C&S Dispute Portal (VCM) credentials. These may differ from your vendor portal username.
        </p>
        <Input label="Username" type="text" name="username" required={true} bind={form.username} noAutofill />
        <PasswordInput label="Password" required={true} bind={form.password} noAutofill />
        <div class="text-sm text-gray-500">
          <p>
            Dispute portal login:{" "}
            <a class="text-gray-900 underline" href={CANDS_DISPUTE_PORTAL_URL} target="_blank" rel="noopener noreferrer">
              {CANDS_DISPUTE_PORTAL_URL}
            </a>
          </p>
        </div>
        <div class="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => setLocation(redirectTo)} disabled={loading}>
            Skip
          </Button>
          <Button
            type="submit"
            class="bg-gray-900 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
            disabled={loading}>
            {loading ? "Saving..." : "Save Credentials"}
          </Button>
        </div>
      </Form>
    </CenterColumnContainer>
  )
}
